<template>
  <busy-button class="w-44"
               :variant="variant"
               :id="templateSaveButtonId"
               :is-loading="isBusy"
               :is-disabled="isEmptyStateTree || isDisabled"
               @click="saveTemplate"
  >
    {{ text }}
  </busy-button>
</template>

<script>
import isEmpty from 'ramda/src/isEmpty'
import templateService from '@/services/amio/template.service'
import { ROUTE_NAMES } from '@/router'
import BusyButton from '@/components/common/BusyButton'
import previewModule from '@/store/modules/preview.module'
import busyButtonsModule from '@/store/modules/busy-buttons.module'
import conversationModule from '@/store/modules/conversation.module'
import templateModule from '@/store/modules/template.module'
import errorUtils from '@/utils/error.utils'
import { BUSY_BUTTONS } from '@/utils/busy-buttons.constants'
import chatBubbleModule from '@/store/modules/chat-bubble.module'
import { ITEM_IDS, SAVE_BUTTON_VARIANTS } from '@/utils/editor.constants'
import { SAVE_BUTTON_LABELS } from '@/utils/text.constants'
import variablesModule from '@/store/modules/variables.module'
import answerModule from '@/store/modules/answer.module'
import clone from 'ramda/src/clone'

export default {
  name: 'TemplateSaveButton',

  components: {
    BusyButton
  },

  props: {
    botId: {
      type: String,
      required: true
    },
    isDisabled: {
      type: Boolean,
      required: false
    }
  },

  watch: {
    'template.id'(newId, oldId) {
      window.clearTimeout(this.buttonStateTimeout)
      if (oldId !== ITEM_IDS.NEW) this.setButtonProps()
    }
  },

  data() {
    return {
      templateSaveButtonId: BUSY_BUTTONS.TEMPLATE_SAVE,
      buttonStateTimeout: null,
      archivedVariableRefs: {},
      archivedAnswerRefs: {},
      variant: SAVE_BUTTON_VARIANTS.DEFAULT,
      text: SAVE_BUTTON_LABELS.DEFAULT
    }
  },

  computed: {
    error: previewModule.error,
    template: templateModule.template,
    conversationHashes: conversationModule.getHashes,
    conversationJsons: conversationModule.getJsons,
    stateTree: chatBubbleModule.stateTree,
    variableRefs: variablesModule.variableRefs,
    answerRefs: answerModule.answerRefs,

    rootJson() {
      return conversationModule.getRootJson()
    },

    isBusy() {
      return busyButtonsModule.isButtonBusy(this.templateSaveButtonId)
    },

    isEmptyStateTree() {
      return previewModule.isEmptyStateTree(this.stateTree)
    }
  },

  methods: {
    async saveTemplate() {
      this.saveVariableRefs()
      this.saveAnswerRefs()
      busyButtonsModule.setButtonBusy(this.templateSaveButtonId, true)
      this.error = null

      try {
        if (this.template.id !== ITEM_IDS.NEW) {
          const patchData = {
            conversation_json: this.rootJson,
            conversation_json_hash: conversationModule.getRootJsonHash(),
            referenced_conversations: {},
            referenced_conversations_hashes: {},
            variable_refs: this.archivedVariableRefs,
            answer_refs: this.archivedAnswerRefs
          }
          if (!isEmpty(this.conversationJsons.answers)) {
            patchData.referenced_conversations['answers'] = this.conversationJsons.answers
            patchData.referenced_conversations_hashes['answers'] = this.conversationHashes.answers
          }
          if (!isEmpty(this.conversationJsons.events)) {
            patchData.referenced_conversations['events'] = this.conversationJsons.events
            patchData.referenced_conversations_hashes['events'] = this.conversationHashes.events
          }
          const updatedTemplate = await templateService.update(this.botId, this.template.id, patchData)
          conversationModule.setArchivedConversationJson(updatedTemplate.conversationJson)

          const { answers, events, answersHashes, eventsHashes } = await templateService.getReferencedConversations(this.botId, updatedTemplate.id)
          await Promise.all([
            conversationModule.setTemplatesHashes({ [updatedTemplate.id]: updatedTemplate.conversationJsonHash }),
            conversationModule.setAnswers(answers),
            conversationModule.setAnswersHashes(answersHashes),
            conversationModule.setEvents(events),
            conversationModule.setEventsHashes(eventsHashes)
          ])
        } else {
          const newTemplate = await templateService.create(
            this.botId, this.template.name, this.rootJson, this.archivedVariableRefs, this.archivedAnswerRefs
          )
          conversationModule.setArchivedConversationJson(newTemplate.conversationJson)

          await Promise.all([
            conversationModule.setId(newTemplate.id),
            conversationModule.setTemplates({ [newTemplate.id]: newTemplate.conversationJson }),
            conversationModule.setTemplatesHashes({ [newTemplate.id]: newTemplate.conversationJsonHash })
          ])
          this.template = newTemplate
          this.$emit('process-new-template', newTemplate)
          this.$router.replace({
            name: ROUTE_NAMES.blocks,
            params: {
              botId: this.botId,
              blockId: newTemplate.id
            }
          })
        }
      } catch (err) {
        variablesModule.restoreVariableRefs(this.archivedVariableRefs)
        answerModule.restoreAnswerRefs(this.archivedAnswerRefs)
        errorUtils.handleConversationDesignerError(err, this.botId, this.stateTree, 'Template', this.$route.fullPath)
      } finally {
        this.startButtonStateTimeout()
        busyButtonsModule.setButtonBusy(this.templateSaveButtonId, false)
      }
    },

    startButtonStateTimeout() {
      this.setButtonProps(this.error ? 'ERROR' : 'SUCCESS')
      this.buttonStateTimeout = setTimeout(() => {
        this.setButtonProps()
      }, 3000)
    },

    setButtonProps(buttonState = 'DEFAULT') {
      this.variant = SAVE_BUTTON_VARIANTS[buttonState]
      this.text = SAVE_BUTTON_LABELS[buttonState]
    },

    saveVariableRefs() {
      this.archivedVariableRefs = clone(this.variableRefs)
      variablesModule.resetVariableRefs()
    },

    saveAnswerRefs() {
      this.archivedAnswerRefs = clone(this.answerRefs)
      answerModule.resetAnswerRefs()
    }
  }
}
</script>
